import * as THREE from 'three';
import {
  DEFAULT_SURFACE_NORMAL,
  coneAround,
  deriveImpactFrame,
  reflectIncoming,
} from './impactFrame';
import type { ParticleMaterialProfile } from './profiles';
import { clampEnergy, energyLaunch, mixEnergyCone } from '../../services/energy';
import {
  impactChunkScale,
  impactSpeedScale,
  mixImpactCone,
  resolveImpact,
  type ImpactParams,
} from './impactRegime';
import { mulberry32, plannedChipCount, seedFromBurst } from './spawn';
import { sampleBaseElevation } from './baseElevation';
import type { ArenaXZ, BoxCollider } from './types';

export const DEFAULT_CHUNK_CAP = 48;
export const DEFAULT_CHIP_CAP = 64;
const CHIP_MIN_PX = 2.25;
const CHIP_CLAY: [number, number, number] = [0.52, 0.22, 0.14];
const CHIP_MORTAR: [number, number, number] = [0.40, 0.38, 0.35];

export interface ChunkSpawnRecord {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly vx: number;
  readonly vy: number;
  readonly vz: number;
  readonly hx: number;
  readonly hy: number;
  readonly hz: number;
  readonly wx: number;
  readonly wy: number;
  readonly wz: number;
  readonly qx: number;
  readonly qy: number;
  readonly qz: number;
  readonly qw: number;
  readonly r: number;
  readonly g: number;
  readonly b: number;
  readonly mass: number;
  readonly restitution: number;
  readonly friction: number;
  readonly releaseAt: number;
  readonly seed: number;
  readonly maxBounces: number;
  readonly persistFor: number;
}

export interface ChunkPlanInput {
  readonly position: readonly [number, number, number];
  readonly direction: readonly [number, number, number];
  readonly energy: number;
  readonly chunkMass: number;
  readonly profile: ParticleMaterialProfile;
  readonly burstIndex: number;
  readonly surfaceNormal?: readonly [number, number, number];
  readonly maxCount?: number;
  readonly impact?: ImpactParams;
}

export interface ChipPlanInput {
  readonly position: readonly [number, number, number];
  readonly direction: readonly [number, number, number];
  readonly energy: number;
  readonly mass: number;
  readonly chipMass: number;
  readonly profile: ParticleMaterialProfile;
  readonly burstIndex: number;
  readonly surfaceNormal?: readonly [number, number, number];
  readonly maxCount?: number;
  readonly impact?: ImpactParams;
}

export interface ChunkSplat {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly mass: number;
  readonly materialId: number;
}

export interface ChunkStats {
  readonly live: number;
  readonly airborne: number;
  readonly settled: number;
  readonly held: number;
}

export interface ChunkBody {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  hx: number;
  hy: number;
  hz: number;
  wx: number;
  wy: number;
  wz: number;
  qx: number;
  qy: number;
  qz: number;
  qw: number;
  r: number;
  g: number;
  b: number;
  mass: number;
  restitution: number;
  friction: number;
  releaseAt: number;
  seed: number;
  settled: boolean;
  released: boolean;
  dirty: boolean;
  born: number;
  bounceCount: number;
  maxBounces: number;
  persistFor: number;
  persistUntil: number;
}

export interface ChunkSim {
  readonly mesh: THREE.InstancedMesh;
  readonly chipMesh: THREE.InstancedMesh;
  readonly cap: number;
  readonly chipCap: number;
  enqueue(records: ChunkSpawnRecord[], simTime: number): number;
  enqueueChips(records: ChunkSpawnRecord[], simTime: number): number;
  step(dt: number, simTime: number, camera?: THREE.Camera | null): ChunkSplat[];
  reset(): void;
  stats(): ChunkStats;
  bodies(): readonly ChunkBody[];
  chipBodies(): readonly ChunkBody[];
  dispose(): void;
}

const GRAVITY = 9.81;
const SETTLE_SPEED = 0.16;
const CONTACT_EPS = 0.008;
const BRICK_BASE: [number, number, number] = [0.55, 0.22, 0.14];
const MORTAR_CRUST: [number, number, number] = [0.42, 0.40, 0.37];

function clamp01(x: number): number {
  return Math.min(1, Math.max(0, x));
}

function sdBox(
  px: number,
  py: number,
  pz: number,
  cx: number,
  cy: number,
  cz: number,
  hx: number,
  hy: number,
  hz: number,
): number {
  const qx = Math.abs(px - cx) - hx;
  const qy = Math.abs(py - cy) - hy;
  const qz = Math.abs(pz - cz) - hz;
  const mx = Math.max(qx, 0);
  const my = Math.max(qy, 0);
  const mz = Math.max(qz, 0);
  return Math.hypot(mx, my, mz) + Math.min(Math.max(qx, qy, qz), 0);
}

export function sampleColliderSDF(
  px: number,
  py: number,
  pz: number,
  boxes: readonly BoxCollider[],
): number {
  let d = py;
  for (const b of boxes) {
    d = Math.min(
      d,
      sdBox(px, py, pz, b.center[0], b.center[1], b.center[2], b.half[0], b.half[1], b.half[2]),
    );
  }
  return d;
}

export function colliderGradient(
  px: number,
  py: number,
  pz: number,
  boxes: readonly BoxCollider[],
): [number, number, number] {
  const e = 0.006;
  const dx = sampleColliderSDF(px + e, py, pz, boxes) - sampleColliderSDF(px - e, py, pz, boxes);
  const dy = sampleColliderSDF(px, py + e, pz, boxes) - sampleColliderSDF(px, py - e, pz, boxes);
  const dz = sampleColliderSDF(px, py, pz + e, boxes) - sampleColliderSDF(px, py, pz - e, boxes);
  const len = Math.hypot(dx, dy, dz) || 1;
  return [dx / len, dy / len, dz / len];
}

function integrateQuat(
  qx: number,
  qy: number,
  qz: number,
  qw: number,
  wx: number,
  wy: number,
  wz: number,
  dt: number,
): [number, number, number, number] {
  const hx = wx * dt * 0.5;
  const hy = wy * dt * 0.5;
  const hz = wz * dt * 0.5;
  const nx = qw * hx + qx + qy * hz - qz * hy;
  const ny = qw * hy - qx * hz + qy + qz * hx;
  const nz = qw * hz + qx * hy - qy * hx + qz;
  const nw = qw - qx * hx - qy * hy - qz * hz;
  const len = Math.hypot(nx, ny, nz, nw) || 1;
  return [nx / len, ny / len, nz / len, nw / len];
}

export function plannedChunkCount(
  energy: number,
  profile: ParticleMaterialProfile,
  cap = 1e9,
  impact?: ImpactParams,
): number {
  const [lo, hi] = profile.signature.chunkCountRange;
  if (
    hi <= 0
    || profile.signature.chunkCap <= 0
    || profile.signature.chunkFraction <= 0
  ) return 0;
  const e = clampEnergy(energy);
  let raw: number;
  if (e <= 0.2) {
    // 0 at 0.05, `lo` at 0.2 — pebble-flick may emit no whole bricks.
    const t = (e - 0.05) / (0.2 - 0.05);
    raw = lo * t;
  } else if (e <= 2) {
    const t = (e - 0.2) / 1.8;
    raw = lo + (hi - lo) * t;
  } else {
    const t = Math.sqrt((e - 2) / 3);
    raw = hi + (profile.signature.chunkCap - hi) * 0.35 * t;
  }
  const n = Math.round(raw);
  const scaled = Math.round(n * impactChunkScale(resolveImpact(impact)));
  return Math.max(0, Math.min(scaled, Math.max(0, cap), profile.signature.chunkCap));
}

function randomUnitQuat(rng: () => number): [number, number, number, number] {
  const u1 = rng();
  const u2 = rng();
  const u3 = rng();
  const s1 = Math.sqrt(1 - u1);
  const s2 = Math.sqrt(u1);
  return [
    s1 * Math.sin(2 * Math.PI * u2),
    s1 * Math.cos(2 * Math.PI * u2),
    s2 * Math.sin(2 * Math.PI * u3),
    s2 * Math.cos(2 * Math.PI * u3),
  ];
}

/**
 * Pure chunk planner. Seeded from the burst index (independent stream).
 */
export function planChunkBurst(input: ChunkPlanInput): ChunkSpawnRecord[] {
  const sig = input.profile.signature;
  const imp = resolveImpact(input.impact);
  const count = plannedChunkCount(input.energy, input.profile, input.maxCount ?? 1e9, input.impact);
  if (count === 0 || input.chunkMass <= 0) return [];
  const rng = mulberry32(seedFromBurst(input.burstIndex) ^ 0xc8a7c501);
  const frame = deriveImpactFrame(input.surfaceNormal ?? DEFAULT_SURFACE_NORMAL);
  const energy = energyLaunch(input.energy);
  const dir = input.direction;
  const origin = input.position;
  const wall = frame.upFactor < 0.55;
  const [sLo, sHi] = sig.chunkStragglerRange;
  const [lo] = sig.chunkCountRange;
  const stragglerN = wall && sHi > 0 && count >= lo
    ? sLo + Math.round(rng() * Math.max(0, sHi - sLo))
    : 0;
  const held = new Set<number>();
  while (held.size < stragglerN && held.size < count) {
    held.add(Math.floor(rng() * count));
  }
  const [w0, w1] = sig.chunkStragglerWindow;
  const [ex, ey, ez] = sig.chunkExtent;
  const volumes = new Float32Array(count);
  const sizes: Array<[number, number, number]> = new Array(count);
  for (let i = 0; i < count; i += 1) {
    const v = 1 + (rng() * 2 - 1) * sig.chunkSizeVariance;
    const half = rng() < sig.chunkHalfChance;
    const sx = ex * v * (half ? 0.5 : 1);
    const sy = ey * v;
    const sz = ez * v;
    sizes[i] = [sx, sy, sz];
    volumes[i] = sx * sy * sz;
  }
  let volSum = 0;
  for (let i = 0; i < count; i += 1) volSum += volumes[i];
  const coneHalf0 = sig.ejectionConeRad
    ? mixEnergyCone(sig.ejectionConeRad[0], sig.ejectionConeRad[1], input.energy)
    : 0.55;
  const coneHalf = coneHalf0 + (mixImpactCone(coneHalf0, imp) - coneHalf0) * 0.35;
  const speedMul = impactSpeedScale(imp);
  const records: ChunkSpawnRecord[] = new Array(count);
  for (let i = 0; i < count; i += 1) {
    const [sx, sy, sz] = sizes[i];
    const reflect = reflectIncoming(dir, frame.normal);
    const launch = wall ? coneAround(rng, reflect, coneHalf * 0.72) : coneAround(rng, dir, 0.45);
    const speed = (1.05 + energy * 1.55) * sig.chunkVelScale * (0.55 + rng() * 0.6) * speedMul;
    let vx = launch[0] * speed;
    let vy = launch[1] * speed - sig.chunkDownBias * (0.55 + rng() * 0.7) * (1 - imp.blastFraction * 0.55);
    let vz = launch[2] * speed;
    if (wall) {
      vx += frame.normal[0] * (0.15 + rng() * 0.2);
      vz += frame.normal[2] * (0.15 + rng() * 0.2);
    }
    if (imp.blastFraction > 0) {
      const kick = (0.85 + 1.35 * energy) * imp.blastFraction;
      vx += frame.normal[0] * kick;
      vy += 0.35 * energy * imp.blastFraction;
      vz += frame.normal[2] * kick;
    }
    const [qx, qy, qz, qw] = randomUnitQuat(rng);
    const tumble = 5 + rng() * 9;
    const wx = (rng() * 2 - 1) * tumble;
    const wy = (rng() * 2 - 1) * tumble * 0.7;
    const wz = (rng() * 2 - 1) * tumble;
    const crust = 0.08 + rng() * 0.22;
    const r = BRICK_BASE[0] + (MORTAR_CRUST[0] - BRICK_BASE[0]) * crust;
    const g = BRICK_BASE[1] + (MORTAR_CRUST[1] - BRICK_BASE[1]) * crust;
    const b = BRICK_BASE[2] + (MORTAR_CRUST[2] - BRICK_BASE[2]) * crust;
    const jitter = 0.03 + rng() * 0.04;
    records[i] = {
      x: origin[0] + (rng() * 2 - 1) * jitter + frame.normal[0] * 0.04,
      y: origin[1] + (rng() * 2 - 1) * jitter * 0.4,
      z: origin[2] + (rng() * 2 - 1) * jitter + frame.normal[2] * 0.04,
      vx,
      vy,
      vz,
      hx: sx * 0.5,
      hy: sy * 0.5,
      hz: sz * 0.5,
      wx,
      wy,
      wz,
      qx,
      qy,
      qz,
      qw,
      r: clamp01(r),
      g: clamp01(g),
      b: clamp01(b),
      mass: input.chunkMass * (volumes[i] / Math.max(volSum, 1e-12)),
      restitution: sig.chunkRestitution,
      friction: sig.chunkFriction,
      releaseAt: held.has(i) ? w0 + rng() * Math.max(0, w1 - w0) : 0,
      seed: rng(),
      maxBounces: 0,
      persistFor: 0,
    };
  }
  return records;
}

/**
 * Angular mid-population (blunt corner / mortar chips). Independent seed
 * stream from whole-brick chunks. Count is ~15% of the grain census,
 * hard-capped by `signature.chipCap`.
 */
export function planChipBurst(input: ChipPlanInput): ChunkSpawnRecord[] {
  const sig = input.profile.signature;
  const imp = resolveImpact(input.impact);
  const count = plannedChipCount(input.mass, input.profile, input.maxCount ?? 1e9, input.energy, input.impact);
  if (count === 0 || input.chipMass <= 0) return [];
  const rng = mulberry32(seedFromBurst(input.burstIndex) ^ 0x51c1c0de);
  const frame = deriveImpactFrame(input.surfaceNormal ?? DEFAULT_SURFACE_NORMAL);
  const energy = energyLaunch(input.energy);
  const dir = input.direction;
  const origin = input.position;
  const wall = frame.upFactor < 0.55;
  const coneHalf0 = sig.ejectionConeRad
    ? mixEnergyCone(sig.ejectionConeRad[0], sig.ejectionConeRad[1], input.energy)
    : 0.55;
  const coneHalf = mixImpactCone(coneHalf0, imp);
  const speedMul = impactSpeedScale(imp);
  const [p0, p1] = sig.chipPersistRange;
  const volumes = new Float32Array(count);
  const sizes: Array<[number, number, number]> = new Array(count);
  for (let i = 0; i < count; i += 1) {
    const corner = rng() < 0.1;
    const long = corner ? 0.030 + rng() * 0.010 : 0.005 + rng() * 0.010;
    const thick = long * (0.28 + rng() * 0.25);
    const width = long * (0.45 + rng() * 0.40);
    sizes[i] = [long, thick, width];
    volumes[i] = long * thick * width;
  }
  let volSum = 0;
  for (let i = 0; i < count; i += 1) volSum += volumes[i];
  const records: ChunkSpawnRecord[] = new Array(count);
  for (let i = 0; i < count; i += 1) {
    const [sx, sy, sz] = sizes[i];
    const reflect = reflectIncoming(dir, frame.normal);
    const launch = wall ? coneAround(rng, reflect, coneHalf * 0.85) : coneAround(rng, dir, 0.50);
    const speed = (1.8 + energy * 2.4) * (0.55 + rng() * 0.55) * speedMul;
    let vx = launch[0] * speed;
    let vy = launch[1] * speed - 0.85 * (0.4 + rng() * 0.6) * (1 - imp.blastFraction * 0.55);
    let vz = launch[2] * speed;
    if (wall) {
      vx += frame.normal[0] * (0.20 + rng() * 0.25);
      vz += frame.normal[2] * (0.20 + rng() * 0.25);
    }
    if (imp.blastFraction > 0) {
      const kick = (1.1 + 1.6 * energy) * imp.blastFraction;
      vx += frame.normal[0] * kick;
      vy += 0.45 * energy * imp.blastFraction;
      vz += frame.normal[2] * kick;
    }
    const [qx, qy, qz, qw] = randomUnitQuat(rng);
    const tumble = 8 + rng() * 14;
    const mortar = rng() < sig.chipMortarChance;
    const tint = mortar ? CHIP_MORTAR : CHIP_CLAY;
    const jitter = 0.02 + rng() * 0.03;
    const ring = 0.08 + rng() * 0.28;
    const phi = rng() * Math.PI * 2;
    const tx = frame.tangent[0] * Math.cos(phi) + frame.bitangent[0] * Math.sin(phi);
    const tz = frame.tangent[2] * Math.cos(phi) + frame.bitangent[2] * Math.sin(phi);
    records[i] = {
      x: origin[0] + (rng() * 2 - 1) * jitter + frame.normal[0] * 0.05 + tx * ring,
      y: origin[1] + (rng() * 2 - 1) * jitter * 0.35,
      z: origin[2] + (rng() * 2 - 1) * jitter + frame.normal[2] * 0.05 + tz * ring,
      vx,
      vy,
      vz,
      hx: sx * 0.5,
      hy: sy * 0.5,
      hz: sz * 0.5,
      wx: (rng() * 2 - 1) * tumble,
      wy: (rng() * 2 - 1) * tumble * 0.7,
      wz: (rng() * 2 - 1) * tumble,
      qx,
      qy,
      qz,
      qw,
      r: tint[0],
      g: tint[1],
      b: tint[2],
      mass: input.chipMass * (volumes[i] / Math.max(volSum, 1e-12)),
      restitution: sig.chipRestitution,
      friction: 0.62,
      releaseAt: 0,
      seed: rng(),
      maxBounces: sig.chipMaxBounces,
      persistFor: p0 + rng() * Math.max(0, p1 - p0),
    };
  }
  return records;
}

function makeChipGeometry(): THREE.BufferGeometry {
  // Flattened irregular tetra / blunt wedge. Not a disc, not a box.
  const verts: Array<[number, number, number]> = [
    [0.56, 0.10, 0.16],
    [-0.48, 0.07, 0.38],
    [-0.14, 0.09, -0.52],
    [0.08, -0.20, 0.06],
  ];
  const faces = [
    [0, 1, 2],
    [0, 3, 1],
    [1, 3, 2],
    [2, 3, 0],
  ];
  const positions = new Float32Array(faces.length * 9);
  const normals = new Float32Array(faces.length * 9);
  for (let f = 0; f < faces.length; f += 1) {
    const [ia, ib, ic] = faces[f];
    const a = verts[ia];
    const b = verts[ib];
    const c = verts[ic];
    const e1x = b[0] - a[0];
    const e1y = b[1] - a[1];
    const e1z = b[2] - a[2];
    const e2x = c[0] - a[0];
    const e2y = c[1] - a[1];
    const e2z = c[2] - a[2];
    let nx = e1y * e2z - e1z * e2y;
    let ny = e1z * e2x - e1x * e2z;
    let nz = e1x * e2y - e1y * e2x;
    const len = Math.hypot(nx, ny, nz) || 1;
    nx /= len;
    ny /= len;
    nz /= len;
    const o = f * 9;
    positions[o] = a[0];
    positions[o + 1] = a[1];
    positions[o + 2] = a[2];
    positions[o + 3] = b[0];
    positions[o + 4] = b[1];
    positions[o + 5] = b[2];
    positions[o + 6] = c[0];
    positions[o + 7] = c[1];
    positions[o + 8] = c[2];
    for (let k = 0; k < 3; k += 1) {
      normals[o + k * 3] = nx;
      normals[o + k * 3 + 1] = ny;
      normals[o + k * 3 + 2] = nz;
    }
  }
  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geom.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
  return geom;
}

function chipScreenScale(
  camera: THREE.Camera | null | undefined,
  x: number,
  y: number,
  z: number,
  worldSize: number,
): number {
  if (!camera || worldSize <= 0) return 1;
  const dist = Math.hypot(
    camera.position.x - x,
    camera.position.y - y,
    camera.position.z - z,
  );
  const persp = camera as THREE.PerspectiveCamera;
  const fov = ((persp.fov ?? 50) * Math.PI) / 180;
  const viewH = 2 * Math.max(dist, 0.05) * Math.tan(fov * 0.5);
  const px = (worldSize / Math.max(viewH, 1e-6)) * 720;
  if (px >= CHIP_MIN_PX) return 1;
  return CHIP_MIN_PX / Math.max(px, 1e-6);
}

function collideBody(
  b: ChunkBody,
  boxes: readonly BoxCollider[],
  surfaceY: number,
): void {
  const radius = 0.42 * (b.hx + b.hy + b.hz) / 1.5;
  const boxD = sampleColliderSDF(b.x, b.y, b.z, boxes);
  const hfD = b.y - surfaceY;
  let nx: number;
  let ny: number;
  let nz: number;
  let d: number;
  if (hfD < boxD) {
    d = hfD;
    nx = 0;
    ny = 1;
    nz = 0;
  } else {
    d = boxD;
    [nx, ny, nz] = colliderGradient(b.x, b.y, b.z, boxes);
  }
  if (d < radius) {
    const push = radius - d;
    b.x += nx * push;
    b.y += ny * push;
    b.z += nz * push;
    const vn = b.vx * nx + b.vy * ny + b.vz * nz;
    if (vn < 0) {
      let rest = b.restitution;
      if (b.maxBounces > 0 && b.bounceCount >= b.maxBounces) rest = 0;
      const fr = b.friction;
      b.vx = (b.vx - nx * vn) * Math.max(0, 1 - fr) - nx * vn * rest;
      b.vy = (b.vy - ny * vn) * Math.max(0, 1 - fr) - ny * vn * rest;
      b.vz = (b.vz - nz * vn) * Math.max(0, 1 - fr) - nz * vn * rest;
      b.wx += (ny * b.vz - nz * b.vy) * 2.2;
      b.wy += (nz * b.vx - nx * b.vz) * 2.2;
      b.wz += (nx * b.vy - ny * b.vx) * 2.2;
      b.wx *= 0.72;
      b.wy *= 0.72;
      b.wz *= 0.72;
      if (b.maxBounces > 0 && b.bounceCount < b.maxBounces) {
        b.bounceCount += 1;
        if (b.bounceCount >= b.maxBounces) b.restitution = 0;
      }
    }
    const speed = Math.hypot(b.vx, b.vy, b.vz);
    if (speed < SETTLE_SPEED && d < radius + CONTACT_EPS) {
      b.vx = 0;
      b.vy = 0;
      b.vz = 0;
      b.wx = 0;
      b.wy = 0;
      b.wz = 0;
      b.settled = true;
      const qlen = Math.hypot(b.qx, b.qy, b.qz, b.qw) || 1;
      b.qx /= qlen;
      b.qy /= qlen;
      b.qz /= qlen;
      b.qw /= qlen;
    }
  }
}

const EMPTY_BOXES: BoxCollider[] = [];

export function stepChunkBodies(
  bodies: ChunkBody[],
  dt: number,
  simTime: number,
  boxes: readonly BoxCollider[],
  sampleHeight: (x: number, z: number) => number,
  opts?: { floorOnlySettle?: boolean },
): void {
  const floorOnly = opts?.floorOnlySettle === true;
  const collideBoxes = floorOnly ? EMPTY_BOXES : boxes;
  const clamped = Math.min(Math.max(dt, 0), 0.05);
  const sub = clamped > 1 / 90 ? 2 : 1;
  const h = clamped / sub;
  for (const b of bodies) {
    if (floorOnly && b.settled && b.y > 0.25) {
      b.settled = false;
      b.dirty = true;
    }
    if (b.settled) continue;
    if (!b.released) {
      if (simTime + 1e-6 < b.releaseAt) continue;
      b.released = true;
      b.dirty = true;
    }
    for (let s = 0; s < sub; s += 1) {
      b.vy -= GRAVITY * h;
      const speed = Math.hypot(b.vx, b.vy, b.vz);
      const damp = 1 / (1 + 0.08 * speed * h);
      b.vx *= damp;
      b.vy *= damp;
      b.vz *= damp;
      b.x += b.vx * h;
      b.y += b.vy * h;
      b.z += b.vz * h;
      const q = integrateQuat(b.qx, b.qy, b.qz, b.qw, b.wx, b.wy, b.wz, h);
      b.qx = q[0];
      b.qy = q[1];
      b.qz = q[2];
      b.qw = q[3];
      const surface = floorOnly ? 0 : sampleHeight(b.x, b.z);
      collideBody(b, collideBoxes, surface);
    }
    b.dirty = true;
  }
}

function hideInstance(mesh: THREE.InstancedMesh, dummy: THREE.Object3D, i: number): void {
  dummy.position.set(0, -200, 0);
  dummy.quaternion.identity();
  dummy.scale.set(0, 0, 0);
  dummy.updateMatrix();
  mesh.setMatrixAt(i, dummy.matrix);
}

function recordToBody(rec: ChunkSpawnRecord, simTime: number): ChunkBody {
  return {
    x: rec.x,
    y: rec.y,
    z: rec.z,
    vx: rec.vx,
    vy: rec.vy,
    vz: rec.vz,
    hx: rec.hx,
    hy: rec.hy,
    hz: rec.hz,
    wx: rec.wx,
    wy: rec.wy,
    wz: rec.wz,
    qx: rec.qx,
    qy: rec.qy,
    qz: rec.qz,
    qw: rec.qw,
    r: rec.r,
    g: rec.g,
    b: rec.b,
    mass: rec.mass,
    restitution: rec.restitution,
    friction: rec.friction,
    releaseAt: rec.releaseAt,
    seed: rec.seed,
    settled: false,
    released: rec.releaseAt <= simTime + 1e-6,
    dirty: true,
    born: simTime,
    bounceCount: 0,
    maxBounces: rec.maxBounces,
    persistFor: rec.persistFor,
    persistUntil: 0,
  };
}

export function createChunkSim(opts: {
  cap?: number;
  chipCap?: number;
  colliders: readonly BoxCollider[];
  arena: ArenaXZ;
  baseElevation: Float32Array;
  baseSize: number;
  floorOnlySettle?: boolean;
}): ChunkSim {
  const cap = Math.max(1, opts.cap ?? DEFAULT_CHUNK_CAP);
  const chipCap = Math.max(1, opts.chipCap ?? DEFAULT_CHIP_CAP);
  const boxes = opts.colliders;
  const floorOnlySettle = opts.floorOnlySettle === true;
  const geom = new THREE.BoxGeometry(1, 1, 1);
  const chipGeom = makeChipGeometry();
  const mat = new THREE.MeshStandardMaterial({
    color: 0xffffff,
    roughness: 0.9,
    metalness: 0.03,
  });
  const chipMat = new THREE.MeshStandardMaterial({
    color: 0xffffff,
    roughness: 0.88,
    metalness: 0.02,
  });
  const mesh = new THREE.InstancedMesh(geom, mat, cap);
  mesh.frustumCulled = false;
  mesh.castShadow = false;
  mesh.receiveShadow = true;
  mesh.name = 'particle-lab-chunks';
  mesh.count = cap;
  const chipMesh = new THREE.InstancedMesh(chipGeom, chipMat, chipCap);
  chipMesh.frustumCulled = false;
  chipMesh.castShadow = false;
  chipMesh.receiveShadow = true;
  chipMesh.name = 'particle-lab-chips';
  chipMesh.count = chipCap;
  const dummy = new THREE.Object3D();
  const color = new THREE.Color();
  for (let i = 0; i < cap; i += 1) {
    hideInstance(mesh, dummy, i);
    color.setRGB(BRICK_BASE[0], BRICK_BASE[1], BRICK_BASE[2]);
    mesh.setColorAt(i, color);
  }
  for (let i = 0; i < chipCap; i += 1) {
    hideInstance(chipMesh, dummy, i);
    color.setRGB(CHIP_CLAY[0], CHIP_CLAY[1], CHIP_CLAY[2]);
    chipMesh.setColorAt(i, color);
  }
  if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  if (chipMesh.instanceColor) chipMesh.instanceColor.needsUpdate = true;
  mesh.instanceMatrix.needsUpdate = true;
  chipMesh.instanceMatrix.needsUpdate = true;

  const bodies: ChunkBody[] = [];
  const chips: ChunkBody[] = [];
  const pendingSplats: ChunkSplat[] = [];
  const sampleHeight = (x: number, z: number) =>
    sampleBaseElevation(opts.baseElevation, opts.baseSize, opts.arena, x, z);

  const writeInstance = (
    inst: THREE.InstancedMesh,
    i: number,
    b: ChunkBody,
    camera?: THREE.Camera | null,
    minScreen = false,
  ) => {
    dummy.position.set(b.x, b.y, b.z);
    dummy.quaternion.set(b.qx, b.qy, b.qz, b.qw);
    const world = Math.max(b.hx, b.hy, b.hz) * 2;
    const boost = minScreen ? chipScreenScale(camera, b.x, b.y, b.z, world) : 1;
    dummy.scale.set(b.hx * 2 * boost, b.hy * 2 * boost, b.hz * 2 * boost);
    dummy.updateMatrix();
    inst.setMatrixAt(i, dummy.matrix);
    color.setRGB(b.r, b.g, b.b);
    inst.setColorAt(i, color);
    b.dirty = false;
  };

  const recycleOldest = (pool: ChunkBody[], inst: THREE.InstancedMesh): ChunkSplat | null => {
    if (pool.length === 0) return null;
    let idx = -1;
    let best = Infinity;
    for (let i = 0; i < pool.length; i += 1) {
      if (!pool[i].settled) continue;
      if (pool[i].born < best) {
        best = pool[i].born;
        idx = i;
      }
    }
    if (idx < 0) {
      idx = 0;
      for (let i = 1; i < pool.length; i += 1) {
        if (pool[i].born < pool[idx].born) idx = i;
      }
    }
    const dead = pool[idx];
    const splat: ChunkSplat = {
      x: dead.x,
      y: dead.y,
      z: dead.z,
      mass: dead.mass,
      materialId: 0,
    };
    hideInstance(inst, dummy, idx);
    const last = pool.length - 1;
    if (idx !== last) {
      pool[idx] = pool[last];
      writeInstance(inst, idx, pool[idx]);
    }
    pool.pop();
    hideInstance(inst, dummy, pool.length);
    return splat;
  };

  const recycleAt = (pool: ChunkBody[], inst: THREE.InstancedMesh, idx: number): ChunkSplat | null => {
    if (idx < 0 || idx >= pool.length) return null;
    const dead = pool[idx];
    const splat: ChunkSplat = {
      x: dead.x,
      y: dead.y,
      z: dead.z,
      mass: dead.mass,
      materialId: 0,
    };
    hideInstance(inst, dummy, idx);
    const last = pool.length - 1;
    if (idx !== last) {
      pool[idx] = pool[last];
      writeInstance(inst, idx, pool[idx]);
    }
    pool.pop();
    hideInstance(inst, dummy, pool.length);
    return splat;
  };

  const pushRecord = (
    pool: ChunkBody[],
    inst: THREE.InstancedMesh,
    poolCap: number,
    rec: ChunkSpawnRecord,
    simTime: number,
  ): boolean => {
    while (pool.length >= poolCap) {
      const s = recycleOldest(pool, inst);
      if (s) pendingSplats.push(s);
      else break;
    }
    if (pool.length >= poolCap) return false;
    const body = recordToBody(rec, simTime);
    pool.push(body);
    writeInstance(inst, pool.length - 1, body);
    if (!body.released) hideInstance(inst, dummy, pool.length - 1);
    return true;
  };

  return {
    mesh,
    chipMesh,
    cap,
    chipCap,
    enqueue(records, simTime) {
      let n = 0;
      for (const rec of records) {
        if (pushRecord(bodies, mesh, cap, rec, simTime)) n += 1;
      }
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
      mesh.instanceMatrix.needsUpdate = true;
      return n;
    },
    enqueueChips(records, simTime) {
      let n = 0;
      for (const rec of records) {
        if (pushRecord(chips, chipMesh, chipCap, rec, simTime)) n += 1;
      }
      if (chipMesh.instanceColor) chipMesh.instanceColor.needsUpdate = true;
      chipMesh.instanceMatrix.needsUpdate = true;
      return n;
    },
    step(dt, simTime, camera = null) {
      const splats = pendingSplats.splice(0, pendingSplats.length);
      stepChunkBodies(bodies, dt, simTime, boxes, sampleHeight, { floorOnlySettle });
      stepChunkBodies(chips, dt, simTime, boxes, sampleHeight, { floorOnlySettle });
      let wrote = splats.length > 0;
      let chipWrote = false;
      for (let i = 0; i < bodies.length; i += 1) {
        const b = bodies[i];
        if (!b.released) {
          hideInstance(mesh, dummy, i);
          wrote = true;
          continue;
        }
        if (b.dirty) {
          writeInstance(mesh, i, b);
          wrote = true;
        }
      }
      for (let i = chips.length - 1; i >= 0; i -= 1) {
        const b = chips[i];
        if (b.settled && b.persistFor > 0 && b.persistUntil <= 0) {
          b.persistUntil = simTime + b.persistFor;
        }
        if (b.settled && b.persistUntil > 0 && simTime >= b.persistUntil) {
          const s = recycleAt(chips, chipMesh, i);
          if (s) splats.push(s);
          chipWrote = true;
          continue;
        }
        if (b.dirty) {
          writeInstance(chipMesh, i, b, camera, true);
          chipWrote = true;
        }
      }
      if (wrote) {
        mesh.instanceMatrix.needsUpdate = true;
        if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
      }
      if (chipWrote) {
        chipMesh.instanceMatrix.needsUpdate = true;
        if (chipMesh.instanceColor) chipMesh.instanceColor.needsUpdate = true;
      }
      return splats;
    },
    reset() {
      bodies.length = 0;
      chips.length = 0;
      pendingSplats.length = 0;
      for (let i = 0; i < cap; i += 1) hideInstance(mesh, dummy, i);
      for (let i = 0; i < chipCap; i += 1) hideInstance(chipMesh, dummy, i);
      mesh.instanceMatrix.needsUpdate = true;
      chipMesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
      if (chipMesh.instanceColor) chipMesh.instanceColor.needsUpdate = true;
    },
    stats() {
      let airborne = 0;
      let settled = 0;
      let held = 0;
      for (const b of bodies) {
        if (!b.released) held += 1;
        else if (b.settled) settled += 1;
        else airborne += 1;
      }
      for (const b of chips) {
        if (!b.released) held += 1;
        else if (b.settled) settled += 1;
        else airborne += 1;
      }
      return { live: bodies.length + chips.length, airborne, settled, held };
    },
    bodies() {
      return bodies;
    },
    chipBodies() {
      return chips;
    },
    dispose() {
      geom.dispose();
      chipGeom.dispose();
      mat.dispose();
      chipMat.dispose();
    },
  };
}
